import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { useAuth } from '../context/AuthContext';
import { useCRM } from '../context/CRMContext';
import { useSettings } from '../context/SettingsContext';
import BaseModal from '../components/modals/BaseModal';
import UserForm from '../components/forms/UserForm';
import { formatCurrency, formatDate, generateId } from '../utils/helpers';

const Settings = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { 
    users, departments, updateDepartment, deleteDepartment, addDepartment, 
    addUser, updateUser, deleteUser, purgeAllData, themeSettings, updateTheme, 
    integrations, toggleIntegration,
    budgets, addBudget, deleteBudget, projects 
  } = useCRM();
  const { crmSettings, updateCrmSettings } = useSettings();

  const [activeTab, setActiveTab] = useState('branding'); 
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  
  const [isBudgetModalOpen, setIsBudgetModalOpen] = useState(false);
  
  // Avansert Budsjett Form State
  const [budgetForm, setBudgetForm] = useState({
    name: '',
    type: 'User', 
    target_id: '',
    start_date: '',
    end_date: '',
    goals: [
      { id: generateId('g'), metric: 'currency', value: 0, unit: 'NOK', project_id: 'all' }
    ]
  });

  // Branding State
  const [localTheme, setLocalTheme] = useState(themeSettings);
  const [activePreset, setActivePreset] = useState('Standard Blå');

  const tabs = [
    { id: 'branding', name: 'Design & Logo', icon: "🎨" },
    { id: 'general', name: 'Generelt', icon: "⚙️" },
    { id: 'organization', name: 'Organisasjon', icon: "🏢" },
    { id: 'users', name: 'Brukere', icon: "👥" },
    { id: 'budgets', name: 'Budsjetter & KPIer', icon: "💰" },
    { id: 'data', name: 'Data & Sikkerhet', icon: "💾" },
  ];

  const metricsOptions = [
    { id: 'currency', label: 'Omsetning (NOK)' },
    { id: 'deals_count', label: 'Antall salg/avtaler (Stk)' },
    { id: 'meetings_count', label: 'Bookede møter (Stk)' },
    { id: 'conversion_rate', label: 'Konverteringsrate (%)' },
    { id: 'hours', label: 'Fakturerbare timer (t)' },
    { id: 'product_count', label: 'Salg av produkt (Stk)' }
  ];

  const handleSaveTheme = () => {
    updateTheme(localTheme);
    alert("Design oppdatert!");
  };

  const handleSaveUser = (userData) => {
    if (editingUser) updateUser(editingUser.user_id, userData);
    else addUser(userData);
    setIsUserModalOpen(false);
  };

  const handleAddGoal = () => {
    setBudgetForm(prev => ({
      ...prev,
      goals: [...prev.goals, { id: generateId('g'), metric: 'currency', value: 0, unit: 'NOK', project_id: 'all' }]
    }));
  };

  const handleRemoveGoal = (goalId) => {
    setBudgetForm(prev => ({
      ...prev,
      goals: prev.goals.filter(g => g.id !== goalId)
    }));
  };

  const handleUpdateGoal = (goalId, field, value) => {
    setBudgetForm(prev => ({
      ...prev,
      goals: prev.goals.map(g => {
        if (g.id === goalId) {
          const updated = { ...g, [field]: value };
          // Auto-set unit based on metric type
          if (field === 'metric') {
            if (value === 'currency') updated.unit = 'NOK';
            else if (value === 'conversion_rate') updated.unit = '%';
            else if (value === 'hours') updated.unit = 'Timer';
            else updated.unit = 'Stk';
          }
          return updated;
        }
        return g;
      })
    }));
  };

  const handleSaveBudget = (e) => {
    e.preventDefault();
    if(budgetForm.goals.length === 0) {
      alert("Du må legge til minst ett mål.");
      return;
    }
    addBudget({ ...budgetForm });
    setIsBudgetModalOpen(false);
    setBudgetForm({
      name: '', type: 'User', target_id: '', start_date: '', end_date: '',
      goals: [{ id: generateId('g'), metric: 'currency', value: 0, unit: 'NOK', project_id: 'all' }]
    });
  };

  const getTargetName = (type, targetId) => {
    if(type === 'User') return users.find(u => u.user_id === targetId)?.name || targetId;
    if(type === 'Department') return departments.find(d => d.department_id === targetId)?.name || targetId;
    if(type === 'Project') return projects.find(p => p.id === targetId)?.name || targetId;
    return targetId;
  };

  const getMetricLabel = (metricId) => {
    return metricsOptions.find(m => m.id === metricId)?.label || metricId;
  };

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-6">
      <h1 className="text-3xl font-bold text-slate-900">Innstillinger</h1>

      <div className="bg-white rounded-lg shadow-sm border border-slate-200 flex min-h-[600px] overflow-hidden">
        {/* Settings Sidebar */}
        <div className="w-64 border-r border-slate-200 p-4 bg-slate-50 shrink-0">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full flex items-center px-3 py-3 mb-1 rounded-lg text-sm font-bold transition-all ${
                activeTab === tab.id ? 'bg-white shadow-sm text-blue-600 border border-slate-100' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <SafeIcon icon={tab.icon} className="w-5 h-5 mr-3" />
              {tab.name}
            </button>
          ))}
        </div>

        {/* Content Area */}
        <div className="flex-1 p-8 overflow-y-auto max-h-[800px] bg-[#1e2532] text-slate-200">
          
          {/* BRANDING TAB */}
          {activeTab === 'branding' && (
             <div className="space-y-8 max-w-4xl">
               <div className="flex justify-between items-center mb-6">
                 <h2 className="text-xl font-black text-white">Design & Logo</h2>
                 <button onClick={handleSaveTheme} className="px-6 py-2 bg-blue-600 text-white rounded-lg font-bold text-sm hover:bg-blue-500 shadow-lg">Lagre Design</button>
               </div>
               {/* Logo Upload */}
               <div className="bg-[#2a303c] p-6 rounded-2xl border border-slate-700">
                 <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                   <SafeIcon icon="🖼️" /> Bedriftslogo
                 </h3>
                 <div className="flex items-center gap-6">
                   <div className="w-24 h-24 bg-white rounded-xl flex items-center justify-center overflow-hidden border-2 border-slate-600 flex-shrink-0 p-2">
                     {localTheme.logoUrl ? (
                       <img src={localTheme.logoUrl} alt="Logo" className="w-full h-full object-contain" />
                     ) : (
                       <span className="text-4xl text-slate-300 font-black">N</span>
                     )}
                   </div>
                   <div className="flex-1">
                     <input 
                       type="text" 
                       placeholder="Lim inn bilde-URL (https://...)" 
                       value={localTheme.logoUrl || ''} 
                       onChange={e => setLocalTheme({...localTheme, logoUrl: e.target.value})}
                       className="w-full bg-[#1e2532] border border-slate-600 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-blue-500 transition-colors"
                     />
                   </div>
                 </div>
               </div>
             </div>
          )}

          {/* GENERELT TAB */}
          {activeTab === 'general' && (
             <div className="space-y-8 max-w-4xl">
               <div className="flex justify-between items-center mb-6">
                 <h2 className="text-xl font-black text-white">Generelle Innstillinger</h2>
               </div>
               {/* Gamification Settings */}
               <div className="bg-[#2a303c] p-6 rounded-2xl border border-slate-700">
                 <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                   <SafeIcon icon="🏆" /> Gamification & Scoreboard
                 </h3>
                 <label className="flex items-center justify-between p-4 border border-slate-600 rounded-xl cursor-pointer hover:bg-white/5 transition-colors">
                   <div>
                     <span className="font-bold text-white text-sm">Aktiver Scoreboard</span>
                     <p className="text-xs text-slate-400">Viser et leaderboard for hele teamet basert på salgsresultater vs budsjett.</p>
                   </div>
                   <input type="checkbox" checked={crmSettings?.enableScoreboard || false} onChange={e => updateCrmSettings({enableScoreboard: e.target.checked})} className="w-5 h-5 text-blue-600 rounded bg-[#1e2532] border-slate-600" />
                 </label>
               </div>
             </div>
          )}

          {/* USERS TAB */}
          {activeTab === 'users' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-white">Brukere</h3>
                <h4 className="text-xl font-black text-white mt-4">Brukeradministrasjon kommer snart</h4>
                <p className="text-sm text-slate-400 mt-2">
                  Brukeradministrasjon for organisasjonsadmin må kobles til sikker tenant-basert invitasjon og rollehåndtering.
                  Inntil dette er klart, administreres brukere av SuperAdmin.
                </p>
              </div>

              {currentUser?.isSuperAdmin ? (
                <div className="bg-[#2a303c] border border-blue-500/30 rounded-xl p-6">
                  <p className="text-sm text-slate-300 mb-4">
                    SuperAdmin kan administrere organisasjoner og brukere fra SuperAdmin-området.
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate('/super-admin/tenants')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold shadow-lg hover:bg-blue-500 transition-colors"
                  >
                    Åpne SuperAdmin brukeradministrasjon
                  </button>
                </div>
              ) : (
                <div className="bg-[#2a303c] border border-amber-500/30 rounded-xl p-6">
                  <p className="text-sm text-amber-100">
                    Brukeradministrasjon for organisasjonsadmin kommer snart.
                  </p>
                </div>
              )}

              {false && (
                <div className="flex justify-between">
                <h3 className="text-lg font-bold text-white">Brukere</h3>
                <button onClick={() => { setEditingUser(null); setIsUserModalOpen(true); }} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold shadow-lg">Ny Bruker</button>
              </div>
              )}
              {false && (
              <div className="bg-[#2a303c] border border-slate-700 rounded-xl overflow-hidden">
                <table className="w-full text-left text-sm">
                  <thead className="bg-[#1e2532] border-b border-slate-700">
                    <tr>
                      <th className="px-6 py-4 text-xs font-black uppercase text-slate-400">Navn</th>
                      <th className="px-6 py-4 text-xs font-black uppercase text-slate-400">Rolle</th>
                      <th className="px-6 py-4 text-right">Handling</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700/50">
                    {users.map(u => (
                      <tr key={u.user_id} className="hover:bg-white/5 transition-colors">
                        <td className="px-6 py-4 font-bold text-white">{u.name}</td>
                        <td className="px-6 py-4"><span className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded text-xs font-bold border border-blue-500/30">{u.role}</span></td>
                        <td className="px-6 py-4 text-right flex justify-end gap-2">
                          <button onClick={() => { setEditingUser(u); setIsUserModalOpen(true); }} className="p-2 hover:bg-white/10 rounded text-slate-400"><SafeIcon icon="✏️" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              )}
            </div>
          )}

          {/* BUDGET TAB */}
          {activeTab === 'budgets' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-bold text-white">Budsjetter & KPI-mål</h3>
                  <p className="text-xs text-slate-400 mt-1">Sett opp komplekse mål og KPIer som teamet skal jobbe mot.</p>
                </div>
                <button onClick={() => setIsBudgetModalOpen(true)} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold shadow-lg flex items-center gap-2">
                  <SafeIcon icon="➕" /> Ny Målsetning
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {budgets.map(b => {
                  // Fallback for old budget format
                  const hasGoals = b.goals && b.goals.length > 0;
                  const primaryCurrencyGoal = hasGoals ? b.goals.find(g => g.metric === 'currency') : null;
                  const displayAmount = primaryCurrencyGoal ? primaryCurrencyGoal.value : (b.amount || 0);
                  
                  return (
                  <div key={b.id} className="bg-[#2a303c] border border-slate-700 p-6 rounded-2xl hover:border-slate-500 transition-all flex flex-col">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h4 className="font-black text-white text-lg">{b.name || 'Uten navn'}</h4>
                        <p className="text-xs text-slate-400 mt-1">
                          <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest border mr-2 ${b.type === 'User' ? 'bg-blue-500/20 text-blue-300 border-blue-500/30' : b.type === 'Department' ? 'bg-purple-500/20 text-purple-300 border-purple-500/30' : 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'}`}>
                            {b.type}
                          </span>
                          Tildelt: <strong className="text-white">{getTargetName(b.type, b.target_id)}</strong>
                        </p>
                      </div>
                      <button onClick={() => deleteBudget(b.id)} className="p-2 hover:bg-red-500/20 hover:text-red-400 rounded-lg text-slate-500 transition-colors">
                        <SafeIcon icon="🗑️" />
                      </button>
                    </div>

                    <div className="flex items-center gap-2 text-xs text-slate-400 mb-6">
                      <SafeIcon icon="📅" className="w-3 h-3" />
                      {formatDate(b.start_date)} — {formatDate(b.end_date)}
                    </div>

                    <div className="space-y-3 mt-auto pt-4 border-t border-slate-700">
                      <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Definerte KPIer</p>
                      
                      {hasGoals ? (
                        b.goals.map(goal => (
                          <div key={goal.id} className="flex justify-between items-center bg-[#1e2532] p-3 rounded-xl border border-slate-700/50">
                            <div className="flex items-center gap-3">
                              <SafeIcon icon={goal.metric === 'currency' ? "💰" : goal.metric === 'meetings_count' ? "📅" : "🎯"} className="text-slate-400 w-4 h-4" />
                              <div>
                                <p className="text-xs font-bold text-slate-300">{getMetricLabel(goal.metric)}</p>
                                <p className="text-[10px] text-slate-500">Gjelder: {goal.project_id === 'all' ? 'Alle prosjekter' : getTargetName('Project', goal.project_id)}</p>
                              </div>
                            </div>
                            <span className="font-black text-white">
                              {goal.metric === 'currency' ? formatCurrency(goal.value) : `${goal.value} ${goal.unit}`}
                            </span>
                          </div>
                        ))
                      ) : (
                        <div className="flex justify-between items-center bg-[#1e2532] p-3 rounded-xl border border-slate-700/50">
                          <div className="flex items-center gap-3">
                            <SafeIcon icon="💰" className="text-slate-400 w-4 h-4" />
                            <p className="text-xs font-bold text-slate-300">Omsetning (Gammel format)</p>
                          </div>
                          <span className="font-black text-white">{formatCurrency(b.amount)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )})}
                {budgets.length === 0 && (
                  <div className="col-span-full py-12 text-center text-slate-500 bg-[#2a303c] rounded-2xl border-2 border-dashed border-slate-700">
                    Ingen budsjetter definert enda.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Fallback for other tabs */}
          {(activeTab !== 'branding' && activeTab !== 'users' && activeTab !== 'general' && activeTab !== 'budgets') && (
            <div className="flex flex-col items-center justify-center h-full text-slate-500">
              <SafeIcon icon="🚧" className="text-4xl mb-4" />
              <p>Innholdet for '{activeTab}' vises her.</p>
            </div>
          )}

        </div>
      </div>

      <BaseModal isOpen={isUserModalOpen} onClose={() => setIsUserModalOpen(false)} title={editingUser ? "Rediger Bruker" : "Ny Bruker"}>
        <UserForm user={editingUser} onClose={() => setIsUserModalOpen(false)} onSave={handleSaveUser} />
      </BaseModal>

      {/* AVANSERT BUDSJETT MODAL */}
      <BaseModal isOpen={isBudgetModalOpen} onClose={() => setIsBudgetModalOpen(false)} title="Opprett Nytt Mål / Budsjett" size="lg">
        <form onSubmit={handleSaveBudget} className="space-y-8">
          
          <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 space-y-4">
            <h3 className="font-black text-slate-900 text-sm uppercase tracking-widest mb-2">1. Hovedinformasjon</h3>
            
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Tittel på målsetning</label>
              <input required type="text" value={budgetForm.name} onChange={e => setBudgetForm({...budgetForm, name: e.target.value})} className="w-full p-3 border border-slate-200 rounded-xl outline-none focus:border-blue-500 font-bold" placeholder="F.eks. Årsbudsjett 2024" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Gjelder for (Nivå)</label>
                <select required value={budgetForm.type} onChange={e => setBudgetForm({...budgetForm, type: e.target.value, target_id: ''})} className="w-full p-3 border border-slate-200 rounded-xl outline-none focus:border-blue-500 bg-white font-medium" >
                  <option value="User">Selger (Individuell)</option>
                  <option value="Department">Avdeling / Team</option>
                  <option value="Company">Hele Selskapet</option>
                </select>
              </div>
              
              {budgetForm.type !== 'Company' && (
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Velg Entitet</label>
                  <select required value={budgetForm.target_id} onChange={e => setBudgetForm({...budgetForm, target_id: e.target.value})} className="w-full p-3 border border-slate-200 rounded-xl outline-none focus:border-blue-500 bg-white font-medium" >
                    <option value="">-- Velg --</option>
                    {budgetForm.type === 'User' && users.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
                    {budgetForm.type === 'Department' && departments.map(d => <option key={d.department_id} value={d.department_id}>{d.name}</option>)}
                  </select>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Fra dato</label>
                <input required type="date" value={budgetForm.start_date} onChange={e => setBudgetForm({...budgetForm, start_date: e.target.value})} className="w-full p-3 border border-slate-200 rounded-xl outline-none focus:border-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Til dato</label>
                <input required type="date" value={budgetForm.end_date} onChange={e => setBudgetForm({...budgetForm, end_date: e.target.value})} className="w-full p-3 border border-slate-200 rounded-xl outline-none focus:border-blue-500" />
              </div>
            </div>
          </div>

          <div className="bg-blue-50/50 p-6 rounded-2xl border border-blue-100 space-y-4">
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-black text-blue-900 text-sm uppercase tracking-widest">2. Måltall / KPIer</h3>
              <button type="button" onClick={handleAddGoal} className="text-xs font-bold text-blue-600 bg-blue-100 hover:bg-blue-200 px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1">
                <SafeIcon icon="➕" /> Legg til KPI
              </button>
            </div>

            {budgetForm.goals.length > 0 ? (
              <div className="space-y-3">
                {budgetForm.goals.map((goal, index) => (
                  <div key={goal.id} className="bg-white p-4 rounded-xl border border-blue-200 shadow-sm flex items-center gap-4 relative group">
                    {budgetForm.goals.length > 1 && (
                      <button type="button" onClick={() => handleRemoveGoal(goal.id)} className="absolute -top-2 -right-2 w-6 h-6 bg-red-100 text-red-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-200">
                        <SafeIcon icon="✕" className="w-3 h-3" />
                      </button>
                    )}
                    
                    <div className="flex-1">
                      <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Måletype</label>
                      <select value={goal.metric} onChange={e => handleUpdateGoal(goal.id, 'metric', e.target.value)} className="w-full p-2 border border-slate-200 rounded-lg text-sm bg-slate-50 outline-none focus:border-blue-500">
                        {metricsOptions.map(opt => <option key={opt.id} value={opt.id}>{opt.label}</option>)}
                      </select>
                    </div>

                    <div className="flex-1">
                      <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Prosjekt / Omfang</label>
                      <select value={goal.project_id} onChange={e => handleUpdateGoal(goal.id, 'project_id', e.target.value)} className="w-full p-2 border border-slate-200 rounded-lg text-sm bg-slate-50 outline-none focus:border-blue-500">
                        <option value="all">Alle (Generelt)</option>
                        {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                    </div>

                    <div className="flex-1">
                      <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Målverdi</label>
                      <div className="relative">
                        <input required type="number" value={goal.value} onChange={e => handleUpdateGoal(goal.id, 'value', Number(e.target.value))} className="w-full p-2 border border-slate-200 rounded-lg text-sm font-black outline-none focus:border-blue-500" placeholder="0" />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">{goal.unit}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 border-2 border-dashed border-blue-200 rounded-xl text-blue-500 font-medium text-sm">
                Ingen KPIer lagt til enda. Klikk på knappen over.
              </div>
            )}
          </div>

          <div className="flex justify-end pt-4 border-t border-slate-100">
            <button type="button" onClick={() => setIsBudgetModalOpen(false)} className="mr-3 px-6 py-3 text-slate-500 font-bold hover:bg-slate-100 rounded-xl transition-colors">Avbryt</button>
            <button type="submit" className="px-8 py-3 bg-slate-900 text-white rounded-xl font-black uppercase tracking-widest hover:bg-slate-800 shadow-xl transition-all">Lagre Budsjett</button>
          </div>
        </form>
      </BaseModal>
    </div>
  );
};

export default Settings;